import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AdminHeader, AdminTitle, AdminBody,
  AdminCard, AdminCardRow, Thumbnail, CardInfo, CardMeta, StatusBadge,
  Btn, BtnRow, EmptyState,
  FormSection, FormTitle, FormGrid, FormField,
  SectionLabel, BackLink, ActionsSection, HelpText,
} from '../styles/AdminStyles';
import { MF_BASE } from '../basePath';
import {
  getAllPostsAdmin,
  createPost,
  updatePost,
  deletePost,
  publishPost,
  uploadBlogImage,
  getBlogImageUrl,
  CATEGORIA_LABELS,
  formatPostDate,
  triggerGerarArtigo,
  getAgentConfigValue,
  setAgentConfigValue,
} from '../services/blogService';
import MarkdownRenderer from '../blog/components/MarkdownRenderer';

const EMPTY_POST = {
  slug: '',
  titulo: '',
  subtitulo: '',
  resumo: '',
  conteudo_md: '',
  categoria: 'guia-bairro',
  tags: [],
  bairro_relacionado: '',
  imagem_capa: '',
  seo_title: '',
  seo_description: '',
  seo_keywords: [],
  status: 'rascunho',
  autor: 'Equipe MF Real Estate',
};

function slugify(s) {
  const base = (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  if (base.length <= 60) return base;
  const cut = base.slice(0, 60);
  const lastDash = cut.lastIndexOf('-');
  return (lastDash > 30 ? cut.slice(0, lastDash) : cut).replace(/-$/, '');
}

export default function BlogAdminPanel({ onExit }) {
  const [view, setView] = useState('list'); // list | form
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showPreview, setShowPreview] = useState(true);
  const [showSeo, setShowSeo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [publishMsg, setPublishMsg] = useState(null); // { type: 'ok'|'err'|'loading', text }
  const [publishing, setPublishing] = useState(false);

  // Modal de configuração do agente
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configForm, setConfigForm] = useState({
    'global.score_minimo_aprovacao': '',
    'global.tamanho_min': '',
    'global.tamanho_max': '',
    'identidade.persona': '',
    'voz.prompt_escrever': '',
    'estrutura.prompt_critica': '',
    'seo.prompt': '',
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configMsg, setConfigMsg] = useState(null);

  // Modal de keywords estáticas SEO
  const [showKeywordsModal, setShowKeywordsModal] = useState(false);
  const [keywordsList, setKeywordsList] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [keywordsSaving, setKeywordsSaving] = useState(false);
  const [keywordsMsg, setKeywordsMsg] = useState(null);

  // Modal "Gerar com agente" — 2 etapas: títulos → artigo
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [agentStep, setAgentStep] = useState('form'); // 'form' | 'titles'
  const [agentForm, setAgentForm] = useState({
    tema: '',
    angulo: '',
    bairro_alvo: '',
    categoria: 'guia-bairro',
    palavra_chave_principal: '',
  });
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [titleScores, setTitleScores] = useState({}); // { titulo: score }
  const [loadingScores, setLoadingScores] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [generatingTitles, setGeneratingTitles] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchMsg, setDispatchMsg] = useState(null);

  // Tracking de runs ativas (persiste em sessionStorage pra sobreviver refresh)
  const ACTIVE_KEY = 'mf_blog_active_dispatches';
  const [activeRuns, setActiveRuns] = useState(() => {
    try {
      const stored = sessionStorage.getItem(ACTIVE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [completedRun, setCompletedRun] = useState(null);

  // Persiste activeRuns
  useEffect(() => {
    try {
      sessionStorage.setItem(ACTIVE_KEY, JSON.stringify(activeRuns));
    } catch {}
  }, [activeRuns]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const data = await getAllPostsAdmin();
    setItems(data);
    setLoading(false);
    return data;
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-poll enquanto houver run ativa
  useEffect(() => {
    if (activeRuns.length === 0) return;

    const checkForCompletedPosts = async () => {
      const data = await getAllPostsAdmin();
      setItems(data);

      // Para cada run ativa, ver se já apareceu um post agente posterior ao dispatch
      const stillActive = [];
      for (const run of activeRuns) {
        const matchingPost = data.find(
          (p) =>
            p.gerado_por === 'agente' &&
            new Date(p.created_at).getTime() >= run.dispatchedAt - 5000 &&
            // match aproximado: bairro ou tema similar
            (p.bairro_relacionado === run.bairro_alvo ||
              (p.titulo || '').toLowerCase().includes((run.tema || '').toLowerCase().slice(0, 20)))
        );

        if (matchingPost) {
          // Run terminou, remove e mostra notificação
          setCompletedRun({
            tema: run.tema,
            postId: matchingPost.id,
            postTitle: matchingPost.titulo,
            score: matchingPost.qualidade_score,
          });
        } else {
          // Timeout: se passou de 15 min, considera órfão e remove
          if (Date.now() - run.dispatchedAt > 15 * 60 * 1000) continue;
          stillActive.push(run);
        }
      }

      if (stillActive.length !== activeRuns.length) {
        setActiveRuns(stillActive);
      }
    };

    // Primeira checagem imediata, depois a cada 20s
    const interval = setInterval(checkForCompletedPosts, 20000);
    return () => clearInterval(interval);
  }, [activeRuns]);

  // Auto-dismiss notificação de run completa após 12s
  useEffect(() => {
    if (!completedRun) return;
    const t = setTimeout(() => setCompletedRun(null), 12000);
    return () => clearTimeout(t);
  }, [completedRun]);

  const filtered = useMemo(() => {
    if (filterStatus === 'all') return items;
    return items.filter((p) => p.status === filterStatus);
  }, [items, filterStatus]);

  const counts = useMemo(() => {
    return {
      all: items.length,
      rascunho: items.filter((p) => p.status === 'rascunho').length,
      revisao: items.filter((p) => p.status === 'revisao').length,
      publicado: items.filter((p) => p.status === 'publicado').length,
    };
  }, [items]);

  // ===== ACTIONS =====
  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_POST);
    setShowSeo(false);
    setView('form');
  };

  const openEdit = (item) => {
    setEditId(item.id);
    setForm({
      ...EMPTY_POST,
      ...item,
      tags: item.tags || [],
      seo_keywords: item.seo_keywords || [],
      // mantém metadados do agente pra exibir
      qualidade_score: item.qualidade_score,
      qualidade_notas: item.qualidade_notas,
      custo_geracao_cents: item.custo_geracao_cents,
      gerado_por: item.gerado_por,
      fontes_pesquisa: item.fontes_pesquisa,
    });
    setShowSeo(false);
    setView('form');
  };

  const handleSave = async () => {
    if (!form.titulo) {
      alert('Título é obrigatório');
      return;
    }
    setSaving(true);

    const payload = {
      ...form,
      slug: slugify(form.slug || form.titulo),
      // se publicando pela 1a vez, marca published_at
      ...(form.status === 'publicado' && !editId ? { published_at: new Date().toISOString() } : {}),
    };
    delete payload.id;
    delete payload.created_at;
    delete payload.updated_at;
    delete payload.views;
    delete payload.qualidade_score;
    delete payload.qualidade_notas;
    delete payload.fontes_pesquisa;
    delete payload.custo_geracao_cents;
    delete payload.faq_jsonld;

    let result;
    if (editId) {
      // se status mudou pra publicado e não tem published_at, preenche
      if (payload.status === 'publicado') {
        const current = items.find((i) => i.id === editId);
        if (current && !current.published_at) {
          payload.published_at = new Date().toISOString();
        }
      }
      result = await updatePost(editId, payload);
    } else {
      result = await createPost(payload);
    }

    setSaving(false);
    if (result.error) {
      alert('Erro ao salvar: ' + result.error.message);
      return;
    }
    setView('list');
    fetchAll();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este artigo? Esta ação não pode ser desfeita.')) return;
    await deletePost(id);
    if (view === 'form') setView('list');
    fetchAll();
  };

  const handleQuickPublish = async (item) => {
    const wasPublished = item.status === 'publicado';
    setPublishing(true);
    setPublishMsg({ type: 'loading', text: wasPublished ? 'Despublicando…' : 'Publicando…' });
    try {
      if (wasPublished) {
        await updatePost(item.id, { status: 'rascunho' });
        setForm((f) => (f && f.id === item.id ? { ...f, status: 'rascunho' } : { ...f, status: 'rascunho' }));
        setPublishMsg({ type: 'ok', text: 'Despublicado' });
      } else {
        await publishPost(item.id);
        setForm((f) => ({ ...f, status: 'publicado', published_at: f.published_at || new Date().toISOString() }));
        setPublishMsg({ type: 'ok', text: 'Publicado' });
      }
      fetchAll();
    } catch (e) {
      setPublishMsg({ type: 'err', text: 'Erro ao salvar' });
    } finally {
      setPublishing(false);
    }
  };

  // Auto-dismiss da mensagem de publish após 3s (mas só quando não está em loading)
  useEffect(() => {
    if (!publishMsg || publishMsg.type === 'loading') return;
    const t = setTimeout(() => setPublishMsg(null), 3000);
    return () => clearTimeout(t);
  }, [publishMsg]);

  // ===== FORM HELPERS =====
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleGenerateSlug = () => {
    setForm((f) => ({ ...f, slug: slugify(f.titulo) }));
  };

  const handleUploadCapa = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const url = await uploadBlogImage(file, 'capas');
    setUploading(false);
    if (url) {
      setForm((f) => ({ ...f, imagem_capa: url }));
    } else {
      alert('Erro ao subir imagem. Verifique se o bucket "mf-blog" existe.');
    }
  };

  const addKeyword = () => {
    const k = keywordInput.trim();
    if (!k) return;
    if (form.seo_keywords.includes(k)) return;
    setForm((f) => ({ ...f, seo_keywords: [...f.seo_keywords, k] }));
    setKeywordInput('');
  };

  const removeKeyword = (i) => {
    setForm((f) => ({ ...f, seo_keywords: f.seo_keywords.filter((_, idx) => idx !== i) }));
  };

  // ===== CONFIG DO AGENTE =====
  useEffect(() => {
    if (!showConfigModal) return;
    setConfigLoading(true);
    setConfigMsg(null);
    const keys = Object.keys(configForm);
    Promise.all(keys.map((k) => getAgentConfigValue(k).then((v) => [k, v || ''])))
      .then((entries) => {
        const obj = Object.fromEntries(entries);
        setConfigForm((prev) => ({ ...prev, ...obj }));
      })
      .finally(() => setConfigLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfigModal]);

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    setConfigMsg(null);
    const entries = Object.entries(configForm);
    const results = await Promise.all(entries.map(([k, v]) => setAgentConfigValue(k, v)));
    setConfigSaving(false);
    if (results.every(Boolean)) {
      setConfigMsg({ type: 'ok', text: 'Configuração salva. Os próximos artigos vão usar os novos valores.' });
    } else {
      setConfigMsg({ type: 'err', text: 'Algumas chaves falharam ao salvar.' });
    }
  };

  // ===== KEYWORDS ESTÁTICAS =====
  useEffect(() => {
    if (!showKeywordsModal) return;
    setKeywordsLoading(true);
    setKeywordsMsg(null);
    getAgentConfigValue('seo.keywords_estaticas')
      .then((value) => {
        const arr = (value || '')
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean);
        setKeywordsList(arr);
      })
      .finally(() => setKeywordsLoading(false));
  }, [showKeywordsModal]);

  const handleAddKeyword = () => {
    const k = newKeyword.trim();
    if (!k) return;
    if (keywordsList.includes(k)) {
      setKeywordsMsg({ type: 'err', text: 'Keyword já existe' });
      return;
    }
    setKeywordsList((prev) => [...prev, k]);
    setNewKeyword('');
    setKeywordsMsg(null);
  };

  const handleRemoveKeyword = (kw) => {
    setKeywordsList((prev) => prev.filter((k) => k !== kw));
  };

  const handleSaveKeywords = async () => {
    setKeywordsSaving(true);
    setKeywordsMsg(null);
    const value = keywordsList.join(', ');
    const ok = await setAgentConfigValue('seo.keywords_estaticas', value);
    setKeywordsSaving(false);
    if (ok) {
      setKeywordsMsg({ type: 'ok', text: 'Keywords salvas. O agente vai usá-las nos próximos artigos.' });
    } else {
      setKeywordsMsg({ type: 'err', text: 'Erro ao salvar. Tente de novo.' });
    }
  };

  // ===== AGENT — ETAPA 1: gerar títulos =====
  const handleGenerateTitles = async () => {
    if (!agentForm.tema.trim()) {
      setDispatchMsg({ type: 'err', text: 'Tema é obrigatório' });
      return;
    }
    setGeneratingTitles(true);
    setDispatchMsg(null);
    try {
      const res = await fetch('/api/generate-titles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(agentForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao gerar títulos');
      const titles = data.titles || [];
      if (titles.length === 0) throw new Error('Nenhum título retornado pelo agente');
      setTitleSuggestions(titles);
      setSelectedTitle(titles[0]);
      setTitleScores({});
      setAgentStep('titles');

      // Busca scores do Google Trends em background (não bloqueia)
      setLoadingScores(true);
      fetch('/api/title-trends', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titles }),
      })
        .then(r => r.json())
        .then(({ results }) => {
          const map = {};
          (results || []).forEach(r => { map[r.title] = r.score; });
          setTitleScores(map);
        })
        .catch(() => {})
        .finally(() => setLoadingScores(false));
    } catch (err) {
      setDispatchMsg({ type: 'err', text: err.message });
    } finally {
      setGeneratingTitles(false);
    }
  };

  // ===== AGENT — ETAPA 2: gerar artigo com título escolhido =====
  const handleDispatchAgent = async () => {
    if (!selectedTitle.trim()) {
      setDispatchMsg({ type: 'err', text: 'Selecione um título' });
      return;
    }
    setDispatching(true);
    setDispatchMsg(null);
    const res = await triggerGerarArtigo({ ...agentForm, titulo_sugerido: selectedTitle });
    setDispatching(false);
    if (res.ok) {
      // Adiciona à lista de runs ativas pro polling
      setActiveRuns((prev) => [
        ...prev,
        {
          runId: res.data.runId || 'sem-id',
          tema: agentForm.tema,
          bairro_alvo: agentForm.bairro_alvo,
          dispatchedAt: Date.now(),
        },
      ]);
      setShowAgentModal(false);
      setDispatchMsg(null);
      setAgentForm({ tema: '', angulo: '', bairro_alvo: '', categoria: 'guia-bairro', palavra_chave_principal: '' });
    } else {
      // Mostra o máximo de detalhes possível pra debug
      const parts = [];
      parts.push(`HTTP ${res.status}`);
      if (res.data?.error) parts.push(res.data.error);
      if (res.data?.message) parts.push(res.data.message);
      if (res.data?.elapsed_ms) parts.push(`(${res.data.elapsed_ms}ms)`);
      if (res.data?.details) {
        const det = res.data.details;
        if (typeof det === 'string') parts.push(det);
        else if (det.message) parts.push(`Trigger.dev: ${det.message}`);
        else if (det.error) parts.push(`Trigger.dev: ${det.error}`);
        else parts.push(`Trigger.dev raw: ${JSON.stringify(det).slice(0, 200)}`);
      }
      if (res.data?.cause) parts.push(`cause: ${res.data.cause}`);
      setDispatchMsg({
        type: 'err',
        text: parts.join(' · '),
      });
      // Log completo no console pra debug
      console.error('[BlogAdmin] dispatch failed', res);
    }
  };

  const dismissActiveRun = (runId) => {
    setActiveRuns((prev) => prev.filter((r) => r.runId !== runId));
  };

  // Helper pra exibir tempo decorrido em formato humano
  const elapsedSince = (ts) => {
    const sec = Math.floor((Date.now() - ts) / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    return `${min}min ${sec % 60}s`;
  };

  // Re-render a cada 1s pra atualizar timer + barra de progresso do banner em tempo real
  const [, setTick] = useState(0);
  useEffect(() => {
    if (activeRuns.length === 0) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [activeRuns.length]);

  // ===== RENDER LIST =====
  if (view === 'list') {
    return (
      <>
        <AdminHeader>
          <BtnRow>
            <BackLink onClick={onExit}>← Imóveis</BackLink>
            <AdminTitle>Blog</AdminTitle>
          </BtnRow>
          <BtnRow>
            <Btn onClick={() => setShowConfigModal(true)}>⚙️ Config Agente</Btn>
            <Btn onClick={() => setShowKeywordsModal(true)}>🔑 Keywords SEO</Btn>
            <Btn onClick={() => setShowAgentModal(true)}>🤖 Gerar com agente</Btn>
            <Btn $variant="primary" onClick={openNew}>+ Novo artigo</Btn>
          </BtnRow>
        </AdminHeader>

        {/* Banner: runs ativas */}
        {activeRuns.length > 0 && (
          <div style={{ padding: '0 24px', marginTop: 16 }}>
            {activeRuns.map((run) => {
              // Calibrado pelas runs reais: ~280s média, com folga de 30s = 310s ETA
              const EXPECTED_TOTAL_S = 310;
              const elapsedMs = Date.now() - run.dispatchedAt;
              const elapsedS = Math.floor(elapsedMs / 1000);
              const remainingS = Math.max(0, EXPECTED_TOTAL_S - elapsedS);
              // Progresso visual: cap em 95% até completar de verdade (evita "100% travado")
              const progress = Math.min(95, (elapsedS / EXPECTED_TOTAL_S) * 100);

              // Fases calibradas em % do tempo total esperado
              // pesquisar: 0-25% · estruturar: 25-35% · escrever: 35-75%
              // criticar: 75-85% · empacotar+save: 85-100%
              const pct = (elapsedS / EXPECTED_TOTAL_S) * 100;
              const phase =
                pct < 25 ? '🔍 Pesquisando na web e lendo o inventário…' :
                pct < 35 ? '📐 Estruturando o artigo (definindo ângulo único)…' :
                pct < 75 ? '✍️ Escrevendo o conteúdo (1200-1800 palavras)…' :
                pct < 85 ? '🎯 Avaliando qualidade (E-E-A-T)…' :
                pct < 100 ? '📦 Gerando SEO meta e salvando…' :
                '⏳ Finalizando (passou do esperado, mas continua rodando)…';

              const fmtTime = (s) => {
                if (s < 60) return `${s}s`;
                const m = Math.floor(s / 60);
                const sec = s % 60;
                return `${m}min ${sec.toString().padStart(2, '0')}s`;
              };

              return (
                <div
                  key={run.runId}
                  style={{
                    background: 'linear-gradient(135deg, #E3F2FD 0%, #E8EAF6 100%)',
                    border: '1px solid #90CAF9',
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        border: '3px solid #1976D2',
                        borderTopColor: 'transparent',
                        animation: 'mfblog-spin 1s linear infinite',
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0D47A1', marginBottom: 2 }}>
                        🤖 {phase}
                      </div>
                      <div style={{ fontSize: 12, color: '#1565C0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {run.tema}
                      </div>
                    </div>
                    <a
                      href="https://api.abianca.com.br"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: 12,
                        color: '#1565C0',
                        textDecoration: 'underline',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Ver logs ↗
                    </a>
                    <button
                      onClick={() => dismissActiveRun(run.runId)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#1565C0',
                        cursor: 'pointer',
                        fontSize: 18,
                        padding: '0 4px',
                      }}
                      title="Ocultar (run continua rodando no servidor)"
                    >
                      ×
                    </button>
                  </div>

                  {/* Barra de progresso */}
                  <div
                    style={{
                      height: 8,
                      background: 'rgba(25, 118, 210, 0.15)',
                      borderRadius: 999,
                      overflow: 'hidden',
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${progress}%`,
                        background: 'linear-gradient(90deg, #1976D2 0%, #42A5F5 100%)',
                        borderRadius: 999,
                        transition: 'width 1s ease-out',
                      }}
                    />
                  </div>

                  {/* Timer + ETA */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#1565C0', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    <span>decorrido: {fmtTime(elapsedS)}</span>
                    <span>
                      {remainingS > 0
                        ? `restam ~${fmtTime(remainingS)}`
                        : 'finalizando, pode demorar mais um pouco…'}
                    </span>
                  </div>
                </div>
              );
            })}
            <style>{`
              @keyframes mfblog-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {/* Banner: run completa */}
        {completedRun && (
          <div style={{ padding: '0 24px', marginTop: 16 }}>
            <div
              style={{
                background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                border: '1px solid #66BB6A',
                borderRadius: 12,
                padding: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 16,
              }}
            >
              <div style={{ fontSize: 28 }}>✅</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1B5E20', marginBottom: 2 }}>
                  Artigo pronto pra revisão
                </div>
                <div style={{ fontSize: 13, color: '#2E7D32' }}>
                  <strong>{completedRun.postTitle}</strong>
                  {completedRun.score != null && ` · qualidade ${completedRun.score}/100`}
                </div>
              </div>
              <Btn
                $variant="primary"
                onClick={() => {
                  const post = items.find((p) => p.id === completedRun.postId);
                  if (post) {
                    openEdit(post);
                    setCompletedRun(null);
                  }
                }}
              >
                Abrir
              </Btn>
              <button
                onClick={() => setCompletedRun(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2E7D32',
                  cursor: 'pointer',
                  fontSize: 18,
                  padding: '0 4px',
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Modal de Configuração do Agente */}
        {showConfigModal && (
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: 16,
            }}
            onClick={() => !configSaving && setShowConfigModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 16, padding: 32,
                maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}
            >
              <h2 style={{ margin: '0 0 4px', fontWeight: 600 }}>⚙️ Configuração do agente</h2>
              <p style={{ margin: '0 0 24px', fontSize: 13, color: '#666' }}>
                Ajuste prompts e parâmetros que controlam a qualidade dos artigos gerados.
                Mudanças entram em vigor no próximo artigo.
              </p>

              {configLoading ? (
                <p style={{ fontSize: 13, color: '#999' }}>Carregando configuração...</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div style={{ padding: 14, background: '#f8f9fb', borderRadius: 10 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Parâmetros</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                      <label style={{ fontSize: 12 }}>
                        Score mínimo (0-100)
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={configForm['global.score_minimo_aprovacao']}
                          onChange={(e) => setConfigForm((f) => ({ ...f, 'global.score_minimo_aprovacao': e.target.value }))}
                          style={{ width: '100%', marginTop: 4, padding: 8, border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        />
                        <span style={{ fontSize: 11, color: '#999' }}>Default: 70 · sugerido: 80</span>
                      </label>
                      <label style={{ fontSize: 12 }}>
                        Tamanho mínimo (palavras)
                        <input
                          type="number"
                          min="500"
                          value={configForm['global.tamanho_min']}
                          onChange={(e) => setConfigForm((f) => ({ ...f, 'global.tamanho_min': e.target.value }))}
                          style={{ width: '100%', marginTop: 4, padding: 8, border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        />
                        <span style={{ fontSize: 11, color: '#999' }}>Default: 1200</span>
                      </label>
                      <label style={{ fontSize: 12 }}>
                        Tamanho máximo
                        <input
                          type="number"
                          value={configForm['global.tamanho_max']}
                          onChange={(e) => setConfigForm((f) => ({ ...f, 'global.tamanho_max': e.target.value }))}
                          style={{ width: '100%', marginTop: 4, padding: 8, border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        />
                        <span style={{ fontSize: 11, color: '#999' }}>Default: 1800</span>
                      </label>
                    </div>
                  </div>

                  <label style={{ fontSize: 13 }}>
                    <strong>Persona</strong> — quem é o agente, tom, perspectiva
                    <textarea
                      rows={4}
                      value={configForm['identidade.persona']}
                      onChange={(e) => setConfigForm((f) => ({ ...f, 'identidade.persona': e.target.value }))}
                      placeholder="Ex: Você é especialista em Alphaville/Barueri com 10 anos de experiência..."
                      style={{ width: '100%', marginTop: 4, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </label>

                  <label style={{ fontSize: 13 }}>
                    <strong>Prompt — Escrever</strong> — instruções de voz, tom e regras de escrita
                    <textarea
                      rows={6}
                      value={configForm['voz.prompt_escrever']}
                      onChange={(e) => setConfigForm((f) => ({ ...f, 'voz.prompt_escrever': e.target.value }))}
                      placeholder="Regras: SEMPRE listar metragem, preço e características de imóveis citados; tom de especialista, não comercial; citar fontes com URL; ..."
                      style={{ width: '100%', marginTop: 4, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </label>

                  <label style={{ fontSize: 13 }}>
                    <strong>Prompt — Crítica (Quality Gate)</strong> — critérios E-E-A-T
                    <textarea
                      rows={5}
                      value={configForm['estrutura.prompt_critica']}
                      onChange={(e) => setConfigForm((f) => ({ ...f, 'estrutura.prompt_critica': e.target.value }))}
                      style={{ width: '100%', marginTop: 4, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </label>

                  <label style={{ fontSize: 13 }}>
                    <strong>Prompt — SEO</strong> — geração de meta tags e slug
                    <textarea
                      rows={4}
                      value={configForm['seo.prompt']}
                      onChange={(e) => setConfigForm((f) => ({ ...f, 'seo.prompt': e.target.value }))}
                      style={{ width: '100%', marginTop: 4, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </label>
                </div>
              )}

              {configMsg && (
                <div style={{
                  marginTop: 16, padding: 12, borderRadius: 8, fontSize: 13,
                  background: configMsg.type === 'ok' ? '#E8F5E9' : '#FFEBEE',
                  color: configMsg.type === 'ok' ? '#2E7D32' : '#C62828',
                }}>
                  {configMsg.text}
                </div>
              )}

              <BtnRow style={{ marginTop: 24, justifyContent: 'flex-end' }}>
                <Btn onClick={() => setShowConfigModal(false)} disabled={configSaving}>Fechar</Btn>
                <Btn $variant="primary" onClick={handleSaveConfig} disabled={configSaving || configLoading}>
                  {configSaving ? 'Salvando...' : 'Salvar configuração'}
                </Btn>
              </BtnRow>
            </div>
          </div>
        )}

        {/* Modal de Keywords SEO estáticas */}
        {showKeywordsModal && (
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: 16,
            }}
            onClick={() => !keywordsSaving && setShowKeywordsModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 16, padding: 32,
                maxWidth: 580, width: '100%', maxHeight: '90vh', overflow: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}
            >
              <h2 style={{ margin: '0 0 4px', fontWeight: 600 }}>🔑 Keywords SEO estáticas</h2>
              <p style={{ margin: '0 0 24px', fontSize: 13, color: '#666' }}>
                Lista de palavras-chave que o agente vai considerar em <strong>todos os artigos gerados</strong>,
                além da keyword específica de cada pauta. Use para reforçar termos do negócio.
              </p>

              {keywordsLoading ? (
                <p style={{ fontSize: 13, color: '#999' }}>Carregando...</p>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <input
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddKeyword(); } }}
                      placeholder="Ex: imóveis alphaville"
                      style={{
                        flex: 1, padding: 10, border: '1px solid #ddd',
                        borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
                      }}
                    />
                    <Btn onClick={handleAddKeyword} disabled={!newKeyword.trim()}>+ Adicionar</Btn>
                  </div>

                  {keywordsList.length === 0 ? (
                    <p style={{ fontSize: 13, color: '#999', fontStyle: 'italic' }}>
                      Nenhuma keyword cadastrada ainda.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {keywordsList.map((kw, i) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '10px 14px', background: '#f5f5f7',
                            borderRadius: 8, fontSize: 14,
                          }}
                        >
                          <span>{kw}</span>
                          <button
                            onClick={() => handleRemoveKeyword(kw)}
                            style={{
                              background: 'transparent', border: 'none',
                              cursor: 'pointer', color: '#c00', fontSize: 18,
                              padding: '0 6px',
                            }}
                            aria-label={`Remover ${kw}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {keywordsMsg && (
                <div style={{
                  marginTop: 16, padding: 12, borderRadius: 8, fontSize: 13,
                  background: keywordsMsg.type === 'ok' ? '#E8F5E9' : '#FFEBEE',
                  color: keywordsMsg.type === 'ok' ? '#2E7D32' : '#C62828',
                }}>
                  {keywordsMsg.text}
                </div>
              )}

              <BtnRow style={{ marginTop: 24, justifyContent: 'flex-end' }}>
                <Btn onClick={() => setShowKeywordsModal(false)} disabled={keywordsSaving}>
                  Fechar
                </Btn>
                <Btn $variant="primary" onClick={handleSaveKeywords} disabled={keywordsSaving || keywordsLoading}>
                  {keywordsSaving ? 'Salvando...' : 'Salvar'}
                </Btn>
              </BtnRow>
            </div>
          </div>
        )}

        {/* Modal de dispatch do agente — 2 etapas */}
        {showAgentModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: 16,
            }}
            onClick={() => !dispatching && !generatingTitles && (setShowAgentModal(false), setAgentStep('form'), setTitleSuggestions([]), setDispatchMsg(null))}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: 32,
                maxWidth: 580,
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}
            >
              {/* ETAPA 1 — formulário */}
              {agentStep === 'form' && (
                <>
                  <h2 style={{ margin: '0 0 4px', fontFamily: 'inherit', fontWeight: 600 }}>
                    🤖 Gerar artigo com agente
                  </h2>
                  <p style={{ margin: '0 0 24px', fontSize: 13, color: '#666' }}>
                    Preencha o tema e o agente vai sugerir 10 títulos otimizados para SEO. Você escolhe o melhor e ele gera o artigo completo.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <label style={{ fontSize: 13 }}>
                      Tema *
                      <input
                        value={agentForm.tema}
                        onChange={(e) => setAgentForm((f) => ({ ...f, tema: e.target.value }))}
                        placeholder="Ex: Vale a pena morar no Alphaville Residencial 12?"
                        style={{ width: '100%', marginTop: 4, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </label>
                    <label style={{ fontSize: 13 }}>
                      Ângulo (opcional)
                      <input
                        value={agentForm.angulo}
                        onChange={(e) => setAgentForm((f) => ({ ...f, angulo: e.target.value }))}
                        placeholder="Ex: para famílias com filhos pequenos"
                        style={{ width: '100%', marginTop: 4, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <label style={{ fontSize: 13 }}>
                        Bairro / condomínio
                        <input
                          value={agentForm.bairro_alvo}
                          onChange={(e) => setAgentForm((f) => ({ ...f, bairro_alvo: e.target.value }))}
                          placeholder="Ex: Alphaville Residencial 12"
                          style={{ width: '100%', marginTop: 4, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                        />
                      </label>
                      <label style={{ fontSize: 13 }}>
                        Categoria
                        <select
                          value={agentForm.categoria}
                          onChange={(e) => setAgentForm((f) => ({ ...f, categoria: e.target.value }))}
                          style={{ width: '100%', marginTop: 4, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14, background: '#fff', boxSizing: 'border-box' }}
                        >
                          {Object.entries(CATEGORIA_LABELS).map(([slug, label]) => (
                            <option key={slug} value={slug}>{label}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label style={{ fontSize: 13 }}>
                      Palavra-chave principal (opcional)
                      <input
                        value={agentForm.palavra_chave_principal}
                        onChange={(e) => setAgentForm((f) => ({ ...f, palavra_chave_principal: e.target.value }))}
                        placeholder="Ex: morar alphaville residencial 12"
                        style={{ width: '100%', marginTop: 4, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </label>
                  </div>

                  {dispatchMsg && (
                    <div style={{ marginTop: 16, padding: 12, borderRadius: 8, fontSize: 13, background: '#FFEBEE', color: '#C62828' }}>
                      {dispatchMsg.text}
                    </div>
                  )}

                  <BtnRow style={{ marginTop: 24, justifyContent: 'flex-end' }}>
                    <Btn onClick={() => { setShowAgentModal(false); setDispatchMsg(null); }} disabled={generatingTitles}>
                      Cancelar
                    </Btn>
                    <Btn $variant="primary" onClick={handleGenerateTitles} disabled={generatingTitles || !agentForm.tema}>
                      {generatingTitles ? '⏳ Gerando títulos...' : 'Sugerir títulos →'}
                    </Btn>
                  </BtnRow>
                </>
              )}

              {/* ETAPA 2 — escolha de título */}
              {agentStep === 'titles' && (
                <>
                  <h2 style={{ margin: '0 0 4px', fontFamily: 'inherit', fontWeight: 600 }}>
                    Escolha o título
                  </h2>
                  <p style={{ margin: '0 0 20px', fontSize: 13, color: '#666' }}>
                    Selecione o título que melhor representa o artigo. {loadingScores ? '⏳ Calculando score SEO...' : '📊 = score SEO/CTR (comprimento, gatilhos, números, especificidade).'}
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {titleSuggestions.map((title, i) => {
                      const score = titleScores[title];
                      const scoreColor =
                        score == null ? '#999' :
                        score >= 50 ? '#2E7D32' :
                        score >= 20 ? '#F57C00' : '#999';
                      return (
                        <button
                          key={i}
                          onClick={() => setSelectedTitle(title)}
                          style={{
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '12px 16px',
                            borderRadius: 8,
                            border: selectedTitle === title ? '2px solid #1a1a2e' : '1px solid #ddd',
                            background: selectedTitle === title ? '#f0f0f8' : '#fff',
                            cursor: 'pointer',
                            fontSize: 14,
                            lineHeight: 1.4,
                            fontWeight: selectedTitle === title ? 600 : 400,
                            transition: 'all 0.15s',
                          }}
                        >
                          <span style={{ color: '#999', fontSize: 12 }}>{i + 1}.</span>
                          <span style={{ flex: 1 }}>{title}</span>
                          <span style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: scoreColor,
                            minWidth: 48,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                          }}>
                            {loadingScores && score == null ? '...' : score == null ? '—' : `📊 ${score}`}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <label style={{ fontSize: 13 }}>
                      Ou edite o título escolhido:
                      <input
                        value={selectedTitle}
                        onChange={(e) => setSelectedTitle(e.target.value)}
                        style={{ width: '100%', marginTop: 4, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </label>
                  </div>

                  {dispatchMsg && (
                    <div style={{
                      marginTop: 16, padding: 12, borderRadius: 8, fontSize: 13,
                      background: dispatchMsg.type === 'ok' ? '#E8F5E9' : '#FFEBEE',
                      color: dispatchMsg.type === 'ok' ? '#2E7D32' : '#C62828',
                    }}>
                      {dispatchMsg.text}
                    </div>
                  )}

                  <BtnRow style={{ marginTop: 24, justifyContent: 'space-between' }}>
                    <Btn onClick={() => { setAgentStep('form'); setDispatchMsg(null); }} disabled={dispatching || generatingTitles}>
                      ← Voltar
                    </Btn>
                    <BtnRow>
                      <Btn onClick={handleGenerateTitles} disabled={dispatching || generatingTitles}>
                        {generatingTitles ? '⏳ Gerando...' : '🔄 Gerar novos'}
                      </Btn>
                      <Btn onClick={() => { setShowAgentModal(false); setAgentStep('form'); setTitleSuggestions([]); setDispatchMsg(null); }} disabled={dispatching || generatingTitles}>
                        Cancelar
                      </Btn>
                      <Btn $variant="primary" onClick={handleDispatchAgent} disabled={dispatching || generatingTitles || !selectedTitle}>
                        {dispatching ? 'Gerando artigo...' : 'Gerar artigo'}
                      </Btn>
                    </BtnRow>
                  </BtnRow>
                </>
              )}
            </div>
          </div>
        )}

        <AdminBody>
          {/* filtros de status */}
          <BtnRow style={{ marginBottom: 16, flexWrap: 'wrap' }}>
            <Btn
              $variant={filterStatus === 'all' ? 'primary' : undefined}
              onClick={() => setFilterStatus('all')}
            >
              Todos ({counts.all})
            </Btn>
            <Btn
              $variant={filterStatus === 'revisao' ? 'primary' : undefined}
              onClick={() => setFilterStatus('revisao')}
            >
              Revisão ({counts.revisao})
            </Btn>
            <Btn
              $variant={filterStatus === 'publicado' ? 'primary' : undefined}
              onClick={() => setFilterStatus('publicado')}
            >
              Publicados ({counts.publicado})
            </Btn>
            <Btn
              $variant={filterStatus === 'rascunho' ? 'primary' : undefined}
              onClick={() => setFilterStatus('rascunho')}
            >
              Rascunhos ({counts.rascunho})
            </Btn>
          </BtnRow>

          {loading ? (
            <EmptyState>Carregando...</EmptyState>
          ) : filtered.length === 0 ? (
            <EmptyState>
              {filterStatus === 'all'
                ? 'Nenhum artigo ainda. Clique em "+ Novo artigo" para começar.'
                : `Nenhum artigo com status "${filterStatus}".`}
            </EmptyState>
          ) : (
            <>
              <SectionLabel>
                {filtered.length} {filtered.length === 1 ? 'artigo' : 'artigos'}
              </SectionLabel>
              <AdminCard>
                {filtered.map((item) => (
                  <AdminCardRow key={item.id} onClick={() => openEdit(item)}>
                    <Thumbnail>
                      {item.imagem_capa && (
                        <img
                          src={getBlogImageUrl(item.imagem_capa)}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      )}
                    </Thumbnail>
                    <CardInfo>
                      <h3>{item.titulo}</h3>
                      <p>
                        {item.categoria && (CATEGORIA_LABELS[item.categoria] || item.categoria)}
                        {item.bairro_relacionado ? ` · ${item.bairro_relacionado}` : ''}
                        {item.published_at ? ` · ${formatPostDate(item.published_at)}` : ''}
                        {item.gerado_por === 'agente' ? ' · 🤖 agente' : ''}
                      </p>
                    </CardInfo>
                    <CardMeta>
                      {item.custo_geracao_cents != null && item.custo_geracao_cents > 0 && (
                        <span
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 10,
                            background: '#F3E5F5',
                            color: '#6A1B9A',
                            fontFamily: 'ui-monospace, Menlo, monospace',
                          }}
                          title="Custo de geração na API Anthropic"
                        >
                          ${(item.custo_geracao_cents / 100).toFixed(2)}
                        </span>
                      )}
                      {item.qualidade_score != null && (
                        <span
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 10,
                            background: item.qualidade_score >= 70 ? '#E8F5E9' : '#FFF3E0',
                            color: item.qualidade_score >= 70 ? '#2E7D32' : '#E65100',
                          }}
                          title="Score de qualidade do quality gate"
                        >
                          {item.qualidade_score}/100
                        </span>
                      )}
                      <StatusBadge $status={item.status}>{item.status}</StatusBadge>
                    </CardMeta>
                  </AdminCardRow>
                ))}
              </AdminCard>
            </>
          )}
        </AdminBody>
      </>
    );
  }

  // ===== RENDER FORM =====
  return (
    <>
      <AdminHeader>
        <BtnRow>
          <BackLink onClick={() => setView('list')}>← Voltar</BackLink>
          <AdminTitle>{editId ? 'Editar artigo' : 'Novo artigo'}</AdminTitle>
        </BtnRow>
        <BtnRow>
          {editId && (
            <Btn $variant="danger" onClick={() => handleDelete(editId)}>
              Excluir
            </Btn>
          )}
          <Btn onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? 'Ocultar preview' : 'Mostrar preview'}
          </Btn>
          <Btn $variant="primary" onClick={handleSave} disabled={saving || !form.titulo}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Btn>
        </BtnRow>
      </AdminHeader>

      <AdminBody>
        {/* Metadados do agente — só aparece se gerado por agente */}
        {form.gerado_por === 'agente' && (
          <FormSection>
            <FormTitle>🤖 Gerado pelo agente</FormTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
              <div style={{ padding: 16, background: '#F5F5F7', borderRadius: 12 }}>
                <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Qualidade
                </div>
                <div style={{
                  fontSize: 28,
                  fontWeight: 600,
                  color: form.qualidade_score >= 70 ? '#2E7D32' : '#E65100',
                }}>
                  {form.qualidade_score ?? '—'}<span style={{ fontSize: 16, color: '#999' }}>/100</span>
                </div>
              </div>
              <div style={{ padding: 16, background: '#F5F5F7', borderRadius: 12 }}>
                <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Custo
                </div>
                <div style={{ fontSize: 28, fontWeight: 600, color: '#6A1B9A', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                  ${((form.custo_geracao_cents || 0) / 100).toFixed(3)}
                </div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  ≈ R$ {(((form.custo_geracao_cents || 0) / 100) * 5).toFixed(2)}
                </div>
              </div>
              <div style={{ padding: 16, background: '#F5F5F7', borderRadius: 12 }}>
                <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Veredito
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#171717', textTransform: 'capitalize' }}>
                  {form.qualidade_notas?.veredito || '—'}
                </div>
              </div>
            </div>

            {/* Scores individuais */}
            {form.qualidade_notas?.scores_individuais && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Scores E-E-A-T:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(form.qualidade_notas.scores_individuais).map(([key, val]) => (
                    <span key={key} style={{
                      fontSize: 12,
                      padding: '6px 12px',
                      background: '#FFF',
                      border: '1px solid #E0E0E0',
                      borderRadius: 8,
                    }}>
                      <strong>{key.replace(/_/g, ' ')}:</strong> {val}/20
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Sugestões de melhoria do quality gate */}
            {form.qualidade_notas?.sugestoes_melhoria?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Sugestões do quality gate:</div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#444' }}>
                  {form.qualidade_notas.sugestoes_melhoria.map((s, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>{s}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Frases problemáticas */}
            {form.qualidade_notas?.frases_problematicas?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Frases marcadas como problemáticas:</div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#C62828' }}>
                  {form.qualidade_notas.frases_problematicas.map((s, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>{s}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Fontes web */}
            {form.fontes_pesquisa?.fatos_web?.length > 0 && (
              <details>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666' }}>
                  Fontes da pesquisa ({form.fontes_pesquisa.fatos_web.length})
                </summary>
                <ul style={{ marginTop: 8, paddingLeft: 20, fontSize: 12, color: '#444' }}>
                  {form.fontes_pesquisa.fatos_web.map((f, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>
                      {f.fato}{' '}
                      {f.fonte && (
                        <a href={f.fonte} target="_blank" rel="noopener noreferrer" style={{ color: '#1976D2' }}>
                          [fonte]
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </FormSection>
        )}

        {/* Básico */}
        <FormSection>
          <FormTitle>Informações básicas</FormTitle>
          <FormGrid>
            <FormField $full>
              <label>Título</label>
              <input
                value={form.titulo}
                onChange={set('titulo')}
                onBlur={() => !form.slug && handleGenerateSlug()}
                placeholder="Ex: Vale a pena morar no Alphaville Residencial 08?"
              />
            </FormField>
            <FormField $full>
              <label>Subtítulo</label>
              <input
                value={form.subtitulo}
                onChange={set('subtitulo')}
                placeholder="Análise honesta de um dos condomínios mais consolidados em 2026"
              />
            </FormField>
            <FormField>
              <label>Slug (URL)</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  value={form.slug}
                  onChange={set('slug')}
                  placeholder="auto-gerado do título"
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                />
                <Btn type="button" onClick={handleGenerateSlug}>↻</Btn>
              </div>
              <HelpText>{MF_BASE}/blog/{form.slug || 'seu-slug'}</HelpText>
            </FormField>
            <FormField>
              <label>Categoria</label>
              <select value={form.categoria} onChange={set('categoria')}>
                {Object.entries(CATEGORIA_LABELS).map(([slug, label]) => (
                  <option key={slug} value={slug}>{label}</option>
                ))}
              </select>
            </FormField>
            <FormField>
              <label>Bairro / condomínio relacionado</label>
              <input
                value={form.bairro_relacionado}
                onChange={set('bairro_relacionado')}
                placeholder="Ex: Alphaville Residencial 08"
              />
              <HelpText>Se bater com o campo "bairro" de algum imóvel publicado, aparece bloco "Imóveis em…" no rodapé do post.</HelpText>
            </FormField>
            <FormField>
              <label>Status</label>
              <select value={form.status} onChange={set('status')}>
                <option value="rascunho">Rascunho</option>
                <option value="revisao">Em revisão</option>
                <option value="publicado">Publicado</option>
              </select>
            </FormField>
            <FormField>
              <label>Autor</label>
              <input value={form.autor} onChange={set('autor')} />
            </FormField>
            <FormField $full>
              <label>Resumo (160 caracteres) — vira meta description e card de listagem</label>
              <textarea
                value={form.resumo}
                onChange={set('resumo')}
                rows={2}
                maxLength={200}
              />
              <HelpText>{(form.resumo || '').length}/160</HelpText>
            </FormField>
          </FormGrid>
        </FormSection>

        {/* Imagem de capa */}
        <FormSection>
          <FormTitle>Imagem de capa</FormTitle>
          <FormGrid>
            <FormField $full>
              {form.imagem_capa && (
                <div style={{ marginBottom: 12 }}>
                  <img
                    src={getBlogImageUrl(form.imagem_capa)}
                    alt=""
                    style={{ maxWidth: 320, maxHeight: 200, borderRadius: 8, objectFit: 'cover' }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadCapa}
                  disabled={uploading}
                />
                {uploading && <span style={{ fontSize: 13, color: '#999' }}>Subindo...</span>}
                {form.imagem_capa && (
                  <Btn type="button" onClick={() => setForm((f) => ({ ...f, imagem_capa: '' }))}>
                    Remover
                  </Btn>
                )}
              </div>
              <HelpText style={{ marginTop: 8 }}>
                Ou cole uma URL:
              </HelpText>
              <input
                type="text"
                value={form.imagem_capa}
                onChange={set('imagem_capa')}
                placeholder="https://... ou caminho dentro do bucket mf-blog"
                style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}
              />
            </FormField>
          </FormGrid>
        </FormSection>

        {/* Conteúdo (markdown) com preview lado a lado */}
        <FormSection>
          <FormTitle>Conteúdo (markdown)</FormTitle>
          <HelpText style={{ marginBottom: 12 }}>
            Use ## para subtítulos, ** para negrito, * para itálico, [texto](url) para links.
          </HelpText>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: showPreview ? '1fr 1fr' : '1fr',
              gap: 16,
            }}
          >
            <textarea
              value={form.conteudo_md}
              onChange={set('conteudo_md')}
              rows={28}
              placeholder={`## Por que esse condomínio se destaca\n\nNo nosso portfólio atual...\n\n## Quanto custa morar aqui hoje\n\n...\n\n## FAQ\n\n### Pergunta?\n\nResposta.`}
              style={{
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: 14,
                lineHeight: 1.6,
                width: '100%',
                padding: 16,
                border: '1px solid #E0E0E0',
                borderRadius: 8,
                resize: 'vertical',
                minHeight: 500,
              }}
            />
            {showPreview && (
              <div
                style={{
                  border: '1px solid #E0E0E0',
                  borderRadius: 8,
                  padding: 24,
                  background: '#FAFAFA',
                  overflow: 'auto',
                  maxHeight: 700,
                  fontSize: 15,
                  lineHeight: 1.7,
                }}
              >
                <MarkdownRenderer>{form.conteudo_md}</MarkdownRenderer>
              </div>
            )}
          </div>
          <HelpText style={{ marginTop: 8 }}>
            {(form.conteudo_md || '').split(/\s+/).filter(Boolean).length} palavras
          </HelpText>
        </FormSection>

        {/* SEO (collapsible) */}
        <FormSection>
          <FormTitle
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setShowSeo((v) => !v)}
          >
            SEO {showSeo ? '▾' : '▸'}
          </FormTitle>
          {showSeo && (
            <FormGrid>
              <FormField $full>
                <label>SEO Title (≤60 caracteres)</label>
                <input
                  value={form.seo_title}
                  onChange={set('seo_title')}
                  maxLength={70}
                  placeholder="Auto-gerado do título se vazio"
                />
                <HelpText>{(form.seo_title || '').length}/60</HelpText>
              </FormField>
              <FormField $full>
                <label>SEO Description (≤155 caracteres)</label>
                <textarea
                  value={form.seo_description}
                  onChange={set('seo_description')}
                  rows={2}
                  maxLength={200}
                  placeholder="Auto-gerado do resumo se vazio"
                />
                <HelpText>{(form.seo_description || '').length}/155</HelpText>
              </FormField>
              <FormField $full>
                <label>Keywords</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {form.seo_keywords.map((k, i) => (
                    <span
                      key={i}
                      style={{
                        background: '#F0F0F0',
                        padding: '4px 10px',
                        borderRadius: 12,
                        fontSize: 12,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      {k}
                      <button
                        type="button"
                        onClick={() => removeKeyword(i)}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: 14,
                          color: '#999',
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                    placeholder="Adicionar palavra-chave..."
                  />
                  <Btn type="button" onClick={addKeyword}>+</Btn>
                </div>
              </FormField>
            </FormGrid>
          )}
        </FormSection>

        {/* Ações no fim */}
        {editId && (
          <ActionsSection>
            <BtnRow style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Btn
                  onClick={() => handleQuickPublish({ id: editId, status: form.status })}
                  disabled={publishing}
                >
                  {publishing
                    ? (form.status === 'publicado' ? 'Despublicando…' : 'Publicando…')
                    : (form.status === 'publicado' ? 'Despublicar' : 'Publicar agora')}
                </Btn>
                {publishMsg && (
                  <span
                    style={{
                      fontSize: 13,
                      color:
                        publishMsg.type === 'ok' ? '#2E7D32'
                        : publishMsg.type === 'err' ? '#C62828'
                        : '#666',
                      transition: 'opacity 0.2s',
                    }}
                  >
                    {publishMsg.type === 'ok' && '✓ '}
                    {publishMsg.text}
                  </span>
                )}
              </div>
              <a
                href={`${MF_BASE}/blog/${form.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 13, color: '#666', textDecoration: 'underline' }}
              >
                Abrir no site →
              </a>
            </BtnRow>
          </ActionsSection>
        )}
      </AdminBody>
    </>
  );
}
